import clsx from 'clsx'
import React, { FunctionComponent, isValidElement, ReactNode } from 'react'

import { ColorTypes, getColorVariable } from '../foundation/Colors'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { warn } from '../utils/console'
import { getChildName, getChildProps } from '../utils/react'
import { ARCADE_ICONS_NAMES } from './arcade-icons/constants'
import { ArcadeIconNames } from './ArcadeIcon'
import { ArcadePaymentIconNames } from './ArcadePaymentIcon'
import { BRONTE_ICONS_NAMES } from './bronte-icons/constants'
import { ROLES, SIZES, TYPES, VIEWBOX_MAP } from './constants'
import { IconNames as BronteIconNames } from './Icon'
import { PaymentIconNames } from './PaymentIcon'
import styles from './style.module.scss'
import { getIconType, iconLookup, isPaymentIcon, viewBoxLookup } from './utils'

export type RegularIconNames = BronteIconNames | ArcadeIconNames
export type RegularPaymentIconNames = PaymentIconNames | ArcadePaymentIconNames
export type AllIconNames = RegularIconNames | RegularPaymentIconNames

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type IconSizes = SizesType[SizesKeys]

export type IconColors = ColorTypes

type TypesType = typeof TYPES
type TypesKeys = keyof TypesType
export type IconTypes = TypesType[TypesKeys]

type RolesType = typeof ROLES
type RolesKeys = keyof RolesType
export type IconRoles = RolesType[RolesKeys]

type CSSClasses = keyof typeof styles

export interface CardColorProps {
  blackAndWhite?: boolean
}

export interface IconProps {
  blackAndWhite?: boolean
  color?: IconColors
  description?: string
  name?: AllIconNames
  size?: IconSizes
  title?: string
  role?: string
}

export type IconVariantComponent = FunctionComponent<IconProps>

// TODO: account for different icons when using different sizes (waiting on designer feedback)

export const isChildIcon = (child: ReactNode): boolean => {
  const childName = getIconName(child)
  return (
    typeof childName !== 'undefined' &&
    (Object.values<RegularIconNames>(BRONTE_ICONS_NAMES).includes(childName) || Object.values<RegularIconNames>(ARCADE_ICONS_NAMES).includes(childName))
  )
}

export const getIconName = (child: ReactNode): RegularIconNames | undefined => {
  if (isValidElement(child)) {
    const childName = getChildName(child)
    return (childName === 'Icon' ? child.props.name : childName) as RegularIconNames | undefined
  }
}

export const getIconProps = (child: ReactNode): IconProps => getChildProps(child)

const Icon: IconVariantComponent = ({ blackAndWhite = false, color, description, name, role = ROLES.PRESENTATION, size = SIZES.MEDIUM, title }) => {
  const svgColor = typeof color !== 'undefined' ? getColorVariable(color) : undefined
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const themeClass = useThemeClass()

  if (typeof name === 'undefined') {
    return null
  }

  const hasImgRole = role === ROLES.IMG
  const hasAccessibilityProp = typeof title !== 'undefined'

  if (hasImgRole && !hasAccessibilityProp) {
    warn(`[Core - Icon.${name}] Component is used without accessibility attribute. Please include "title" prop to meet AA compliance.`)
  }

  const isPayment = isPaymentIcon(name)
  const type = getIconType(isPayment, isBronteDesign)

  const ariaHidden = typeof title === 'undefined' && typeof description === 'undefined'
  if (!ariaHidden) {
    role = ROLES.IMG
  }

  const paymentIconClassname = styles[`card${isBronteDesign ? '' : 'Arcade'}${size}` as CSSClasses]
  const containerClassnames = clsx(
    styles.container,
    !isPayment && styles[`icon${size}` as CSSClasses],
    isPayment && paymentIconClassname,
    !isBronteDesign && !isPayment && styles[`iconArcade${size}` as CSSClasses],
    styles[themeClass]
  )

  const renderTitle = (): ReactNode => {
    if (typeof title === 'undefined') {
      return null
    }
    return <title>{title}</title>
  }

  const renderDescription = (): ReactNode => {
    if (typeof description === 'undefined') {
      return null
    }
    return <desc>{description}</desc>
  }

  const renderIcon = (): ReactNode => {
    return iconLookup(name, { blackAndWhite, isBronteDesign })
  }

  const dynamicViewBox = viewBoxLookup(name, isBronteDesign)
  const viewBox = dynamicViewBox ?? VIEWBOX_MAP[type]

  return (
    <span className={containerClassnames} data-testid='icon' data-name={name}>
      <svg
        role={role}
        aria-hidden={ariaHidden}
        width='100%'
        height='100%'
        viewBox={viewBox}
        color={svgColor}
        xmlns='http://www.w3.org/2000/svg'
        data-color={!blackAndWhite}
      >
        {renderTitle()}
        {renderDescription()}
        {renderIcon()}
      </svg>
    </span>
  )
}

export const BaseIcon = Icon
